import React, { useState } from 'react';
import { useTheme } from '@/theme';
import { Text } from '@/components/ui/common/Typography';
interface Tab {
  id: string;
  label: string;
  content: React.ReactNode;
}

interface TabGroup {
  title?: string;
  tabs: Tab[];
}

interface TabsProps {
  tabs?: Tab[];
  groups?: TabGroup[];
  defaultActiveTab?: string;
  onChange?: (tabId: string) => void;
}

export const Tabs: React.FC<TabsProps> = ({ tabs, groups, defaultActiveTab, onChange }) => {
  const { theme } = useTheme();

  // Normalize to groups structure
  const normalizedGroups: TabGroup[] = groups || (tabs ? [{ tabs }] : []);
  const allTabs = normalizedGroups.flatMap(group => group.tabs);

  const [activeTab, setActiveTab] = useState(defaultActiveTab || allTabs[0]?.id || '');
  const [hoveredTab, setHoveredTab] = useState<string | null>(null);

  const handleTabClick = (tabId: string) => {
    setActiveTab(tabId);
    onChange?.(tabId);
  };

  const activeTabContent = allTabs.find(tab => tab.id === activeTab)?.content;

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
      }}
    >
      {/* Tab Headers */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexShrink: 0,
            alignItems: 'flex-start',
            gap: theme.spacing[5],
          }}
        >
          {normalizedGroups.map((group, groupIndex) => (
            <div
              key={groupIndex}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: theme.spacing[2],
                padding: theme.spacing[2],
                border: `1px solid ${theme.colors.border.divider}`,
                borderRadius: theme.borderRadius.md,
              }}
            >
              {group.title && (
                <Text
                  color="textSecondary"
                  size="sm"
                  weight="medium"
                  style={{ paddingRight: theme.spacing[4] }}
                >
                  {group.title}
                </Text>
              )}
              {group.tabs.map(tab => {
                const isActive = activeTab === tab.id;
                const isHovered = hoveredTab === tab.id;

                let borderColor = theme.colors.border.interactive.default;
                if (isActive) {
                  borderColor = theme.colors.border.interactive.selected;
                } else if (isHovered) {
                  borderColor = theme.colors.border.interactive.hover;
                }

                return (
                  <button
                    key={tab.id}
                    onClick={() => handleTabClick(tab.id)}
                    onMouseEnter={() => setHoveredTab(tab.id)}
                    onMouseLeave={() => setHoveredTab(null)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      height: '22px',
                      padding: `${theme.spacing[1]}px ${theme.spacing[4]}px`,
                      borderRadius: theme.borderRadius.md,
                      background: isActive
                        ? theme.colors.surface.interactive.selectedNav
                        : 'transparent',
                      cursor: 'pointer',
                      transition: 'background 0.2s ease',
                    }}
                  >
                    <Text color="textPrimary" size="md" weight="medium">
                      {tab.label}
                    </Text>
                    <div
                      style={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: '2px',
                        background: borderColor,
                        transition: 'background 0.2s ease',
                      }}
                    />
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          paddingTop: theme.spacing[6],
          flexDirection: 'column',
        }}
      >
        {activeTabContent}
      </div>
    </div>
  );
};
